import { Action, AnyAction, ThunkAction, combineReducers, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import api from '../api'
import appSlice from './appSlice'
import authSlice from './authSlice'
import travelAbroadSlice from './travelAbroadSlice'
import tplSlice from './tplSlice'
import healthSlice from './healthSlice'
import criticalIllnessSlice from './criticalIllnessSlice'
import liveChat from './liveChatSlice'
import businessSlice from './businessSlice'
import welcomerSlice from './welcomerSlice'
import petSlice from './petSlice'
import newUser from './newUser'
import internationalTplSlice from './internationalTplSlice'

// redux-persist
import { persistReducer, persistStore } from 'redux-persist'
import storageSession from 'redux-persist/lib/storage/session'

const intlTplPersistConfig = {
  key: 'internationalTpl',
  storage: storageSession,
}

// internationalTpl persist
const persistedIntlTplReducer = persistReducer(intlTplPersistConfig, internationalTplSlice)

const combinedReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  newUser: newUser,
  auth: authSlice,
  liveChat: liveChat,
  app: appSlice,
  travelAbroad: travelAbroadSlice,
  tpl: tplSlice,
  health: healthSlice,
  critical: criticalIllnessSlice,
  welcomer: welcomerSlice,
  business: businessSlice,
  pet: petSlice,
  internationalTpl: persistedIntlTplReducer,
})

export const rootReducer: any = (state: RootState, action: AnyAction) => {
  // clear cache/store on logout)
  if (
    action?.meta?.arg?.endpointName === 'logout' &&
    // check that the logout action is fulfilled, otherwise token will be cleared before sending to backend, and second logout without refresh will give an error (token will be cleared before POST logout request is made
    action?.type === 'api/executeMutation/fulfilled'
  ) {
    // console.log('axxion', action);
    // @ts-ignore
    state = undefined
  }
  return combinedReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(api.middleware),
})

export const persistor = persistStore(store)

// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch)

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch
// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>()
export type AppThunk = ThunkAction<void, RootState, unknown, Action>

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export default store
